/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import PropTypes from "prop-types";
import { CustomLink } from "../../custom-link";
import { SvgHandler } from "../../svg-handler/svg-handler";
import defaultAuthorIcon from "../../../../assets/icons/default-author.svg";
import envelope from "../../../../assets/icons/envelope.svg";
import { getLocaleDate } from "../../../utils";

import "./user-profile.m.css";


const ProfileSkeleton = () => (
  <div styleName="container-wrapper animation">
    <div styleName="load-style">
      <div styleName="load-img" />
    </div>
    <div>
      <div styleName="load-author-name" />
      <div styleName="load-subs-details" />
      <div styleName="load-line" />
      <div styleName="load-email-detail">
        <span styleName="load-email" />
        <p styleName="load-detail" />
      </div>
      <div styleName="load-mob-detail">
        <span styleName="load-mobile" />
        <p styleName="load-detail" />
      </div>
      <div styleName="load-plan-btn" />
    </div>
  </div>
);

const getActivePlan = (subscriptions) => {
  return (
    subscriptions.find(
      (plan) =>
        (plan.active && plan["subscription_type"] === "standard") ||
        (plan.active && plan["subscription_type"] === "group_access")
    ) || {}
  );
};

const UserAvatar = ({ avatarUrl }) => {
  if (avatarUrl) {
    return <img src={avatarUrl} loading="lazy" />;
  }
  return <SvgHandler xlinkHref={defaultAuthorIcon} width="125" height="125" viewBox="0 0 125px 125px" />;
};

UserAvatar.propTypes = {
  avatarUrl: PropTypes.string,
};

const UserSubscriptionDetails = ({ subscriptions, myProfile, localizedMonths }) => {
  if (!subscriptions) return null;


  const {
    end_timestamp: endTimestamp,
    active: isActive,
    plan_name: planName,
    plan_occurrence: planType = "",
  } = getActivePlan(subscriptions);
  const { activePlan: localizedActivePlan = "", noSubscription: localizedNoSubscription = "" } = myProfile;
  const planDetails = planType === "Recurring" ? "Next due date" : "Valid till";
  return isActive ? (
    <div styleName="subs-details" data-testid="subs-details">
      <div styleName="plan-details">{localizedActivePlan || "Active Plan"}</div>
      <div styleName="plan-details">
        <span styleName="plan-name">{planName}</span>
        <span styleName="expire-time">
          ({planDetails} &nbsp;
          {endTimestamp && getLocaleDate(endTimestamp, localizedMonths)  })
        </span>
      </div>
    </div>
  ) : (
    <span styleName="subs-details">{localizedNoSubscription || "No subscriptions yet"}</span>
  );
};

UserSubscriptionDetails.propTypes = {
  subscriptions: PropTypes.array,
  localizedMonths: PropTypes.object,
  myProfile: PropTypes.shape({
    activePlan: PropTypes.string,
    noSubscription: PropTypes.string,
  }),
};

const UserProfile = ({ enableAccesstype, member, subscriptions, isSubscriptionsLoading, myProfile, localizedMonths ={} }) => {
  const { buttonText: localizedButtonText = "" } = myProfile;
  if (isSubscriptionsLoading) <ProfileSkeleton />;
  return (
    <div styleName="container-wrapper">
      <div styleName="author-image">
        <UserAvatar avatarUrl={member["avatar-url"]} />
      </div>
      <div styleName="author-details">
        <h2 styleName="author-name">{member.name}</h2>
        <div styleName="email-detail">
          <span styleName="envelope">
            <SvgHandler xlinkHref={envelope} width="24" height="24" viewBox="0 0 24px 24px" />
          </span>
          <p>{member.email}</p>
        </div>
        {!member["phone-number"] ? null : (
          <div styleName="mob-detail">
            <span>PHONE</span>
            <p>{member["phone-number"]}</p>
          </div>
        )}
        {enableAccesstype && (
          <div styleName="subscription-detail">
            {!isSubscriptionsLoading && <UserSubscriptionDetails subscriptions={subscriptions} myProfile={myProfile} localizedMonths={localizedMonths} />}
            <div styleName="other-detail">
              <div data-testid="plan-btn" styleName="plan-btn">
                <CustomLink href={"/subscription"}>{localizedButtonText || "Upgrade Plan"}</CustomLink>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

UserProfile.propTypes = {
  isAccessTypeLoaded: PropTypes.bool,
  enableAccesstype: PropTypes.bool,
  isSubscriptionsLoading: PropTypes.bool,
  member: PropTypes.shape({
    name: PropTypes.string,
    "avatar-url": PropTypes.string,
    email: PropTypes.string,
    "phone-number": PropTypes.string,
  }),
  subscriptions: PropTypes.array,
  localizedSubscribe: PropTypes.string,
  myProfile: PropTypes.shape({
    buttonText: PropTypes.string,
  }),
};

export { UserProfile };
